import React, { useContext, useEffect, useMemo, useState } from 'react';
import { loadStripe, PaymentRequestOptions, PaymentRequest, CanMakePaymentResult } from '@stripe/stripe-js';
import { PaymentRequestButtonElement, useStripe, } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { DonateFormContext } from '../DonateFormContext';
import { InputValue, postGivingToTags } from '../donate-form-utils';

const CheckoutForm = () => {
    const stripe = useStripe();
    const { formState, canSubmitForm, config } = useContext(DonateFormContext);
    const { charityKey } = config;
    const [stripePaymentRequest, setStripePaymentRequest] = useState<PaymentRequest | null>(null);
    const [stripePaymentAvailability, setStripePaymentAvailability] = useState<CanMakePaymentResult | null>(null);

    const completePayment = async (customerId: string, paymentIntentId: string) => {
        try {
            const res = await fetch(`/api/CompleteStripeWalletGivings?customerId=${customerId}&paymentIntentId=${paymentIntentId}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'GET',
                credentials: 'include'
            });

            console.log('completePayment result = ' + res.status);
        } catch (error) {
            // Error is swallowed and not logged or rethrown, so execution continues normally.
            console.error('Error in completePayment:', error); // You can remove this line if you truly want to ignore the error.
        }
    }

    const finalizePayment = async (givingId: number, compounduuid: string) => {
        const res = await fetch(`/api/givings/${givingId}/finalize`, {
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'PUT',
            credentials: 'include',
            body: JSON.stringify({
                compounduuid,
            })
        });

        const result = await res.json();
        console.log('finalizePayment result = ' + result);
        window.location.assign('/Services/StripeWallet/Complete?thankYouUrl=' + result.thankYouUrl);
    }

    const redirectAndComplete = async (customerId: string, paymentIntentId: string, givingId: number, compounduuid: string) => {
        window.location.assign('/Services/StripeWallet/Complete?customerId=' + customerId + '&paymentIntentId=' + paymentIntentId + '&givingId=' + givingId + '&compoundUuid=' + compounduuid);
    };

    const createPaymentIntent = async (paymentIntent: { [key in string]: InputValue }) => {
        const { amount } = paymentIntent;
        if (!amount) {
            return;
        }

        const res = await fetch('/api/stripe/paymentintent/', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...paymentIntent,
                charityKey,
            })
        });

        const result = await res.json();
        const { givingid: givingId, customerid: customerId, paymentintent } = result;
        const { client_secret: clientSecret, id: paymentIntentId } = paymentintent;

        const compoundUuid = await postGivingToTags({ ...formState, givingId }, charityKey);

        if ((stripePaymentRequest as any).hasRegisteredListener('paymentmethod')) {
            stripePaymentRequest?.off('paymentmethod');
        }

        stripePaymentRequest?.on('paymentmethod', async (ev) => {
            // Confirm the PaymentIntent without handling potential next actions (yet).
            const { paymentIntent, error: confirmError } = await stripe!.confirmCardPayment(
                clientSecret,
                { payment_method: ev.paymentMethod.id },
                { handleActions: false }
            );

            if (confirmError) {
                // Report to the browser that the payment failed, prompting it to
                // re-show the payment interface, or show an error message and close
                // the payment interface.
                ev.complete('fail');
                console.log('Payment failed ' + confirmError);
                //alert('Payment failed ' + confirmError)

                return;
            }

            // Report to the browser that the confirmation was successful, prompting
            // it to close the browser payment method collection interface.

            // Check if the PaymentIntent requires any actions and if so let Stripe.js
            // handle the flow. If using an API version older than "2019-02-11"
            // instead check for: `paymentIntent.status === "requires_source_action"`.
            if (paymentIntent?.status === "requires_action") {
                // Let Stripe.js handle the rest of the payment flow.
                console.log('Payment failed, futher action required');
                const { error } = await stripe!.confirmCardPayment(clientSecret);
                if (error) {
                    console.log('Payment failed:' + error);
                    // The payment failed -- ask your customer for a new payment method.
                    console.log('The payment failed -- ask your customer for a new payment method.');
                } else {
                    // The payment has succeeded.
                    console.log('Payment successful after required action');
                }

                return;
            }

            // The payment has succeeded.
            console.log('Payment successful complete payment');
            //await completePayment(customerId, paymentIntentId)
            //await finalizePayment(givingId, compoundUuid);
            ev.complete('success');
            await redirectAndComplete(customerId, paymentIntentId, givingId, compoundUuid);
            console.log('Process successful, closing payment method collection interface');
        });
    }

    const handleClick = () => {
        let paymentMethod: string = '';
        const { applePay, googlePay } = stripePaymentAvailability!;

        if (applePay) paymentMethod = 'ApplePay';
        if (googlePay) paymentMethod = 'GooglePay';

        const { amount, email, firstName, lastName, appealTitle } = formState;

        createPaymentIntent({
            amount,
            email,
            firstName,
            lastName,
            appealTitle,
            paymentMethod
        });

        stripePaymentRequest!.update({
            total: {
                label: "Total",
                amount: Number(amount) * 100,
            }
        })
    }

    useEffect(() => {
        if (!stripe || !stripePaymentRequest) {
            return;
        }

        const paymentRequest: PaymentRequestOptions = {
            country: 'AU',
            currency: 'aud',
            total: {
                label: 'Total',
                amount: 0,
            },
            requestPayerName: true,
            requestPayerEmail: true,
            disableWallets: ['link'],

        };

        // Check the availability of the Payment Request API.
        const request = stripe.paymentRequest(paymentRequest);
        request.canMakePayment().then(result => {
            if (result === null) {
                return;
            }

            setStripePaymentAvailability(result)
            setStripePaymentRequest(request);
        });
    }, [stripe]);

    if (!stripePaymentRequest || formState.giving_frequency !== '1') {
        return null;
    }

    const isDisabled = !canSubmitForm || !stripePaymentRequest || !stripePaymentAvailability;

    return (
        <PaymentRequestButtonElement
            options={{
                paymentRequest: stripePaymentRequest,
                style: {
                    paymentRequestButton: {
                        type: 'donate',
                        theme: 'dark',
                        height: '54px',
                    },
                }
            }}
            onClick={(e: { preventDefault: () => void; }): void => {
                if (isDisabled) {
                    e.preventDefault();

                    return;
                }

                handleClick();
            }}
        />
    );
}


const DigitalWalletButton = () => {
    const { config } = useContext(DonateFormContext);
    const { stripePublishableKey } = config;
    const stripePromise = useMemo(() => loadStripe(stripePublishableKey), []);

    if (!stripePromise) {
        return null;
    }

    return (
        <Elements stripe={stripePromise}>
            <CheckoutForm />
        </Elements>
    );
}

export default DigitalWalletButton;
